import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_ANAMNESIS } from "../../../../../helpers/helpCenter";
import { entityFormKey } from "../../../../../helpers/entityEditForm";
import {
  ClientRecordTemplate,
  ClientRecordTemplateSchema,
  normalizeSchemaFieldType,
  SchemaFieldDefinition,
  SchemaFieldType,
  copyFromPreset,
  create,
  deleteTemplate,
  getAll,
  getById,
  getTemplatePresets,
  update,
} from "../../../../../services/clientRecordTemplateService";
import { ClientRecordTemplatePreset } from "../../../../../services/clientRecordTemplatePresetService";
import {
  ConsentTerm,
  getConsentTerms,
  updateConsentTerm,
} from "../../../../../services/consentTermService";
import AnamnesisTemplateBuilderFields from "../../../../../Components/Studio/AnamnesisTemplateBuilderFields";
import { getProducts } from "../../../../../services/productService";
import { getServices } from "../../../../../services/serviceService";

const PER_PAGE = 50;

export type BuilderQuestionType =
  | "text"
  | "textarea"
  | "boolean"
  | "select"
  | "section"
  | "consent_text"
  | "consent_checkbox"
  | "signature";

export type BuilderQuestion = {
  label: string;
  type: BuilderQuestionType;
  required: boolean;
  options: string;
  body?: string;
  slug?: string;
  clinical_gate?: boolean;
  clinical_gate_service_ids?: number[];
  clinical_gate_product_ids?: number[];
};

type TemplateFormValues = {
  title: string;
  record_type: "anamnesis" | "evolution";
  questions: BuilderQuestion[];
  is_active: boolean;
};

const QUESTION_TYPES: BuilderQuestionType[] = [
  "text",
  "textarea",
  "boolean",
  "select",
  "section",
  "consent_text",
  "consent_checkbox",
  "signature",
];

const emptyFormValues = (): TemplateFormValues => ({
  title: "",
  record_type: "anamnesis",
  questions: [],
  is_active: true,
});

const slugifyLabel = (label: string, index: number): string => {
  const base = label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

  return base || `field_${index + 1}`;
};

const formatOptionsForInput = (
  options: SchemaFieldDefinition["options"]
): string => {
  if (!Array.isArray(options)) {
    return "";
  }

  return options
    .map((option) => {
      if (typeof option === "string") {
        return option;
      }

      return String(option.value ?? option.label ?? "");
    })
    .filter(Boolean)
    .join(", ");
};

export const schemaToQuestions = (
  schema: ClientRecordTemplateSchema | null | undefined
): BuilderQuestion[] => {
  if (!schema) {
    return [];
  }

  const rawFields = Array.isArray(schema) ? schema : schema.fields ?? [];

  if (!Array.isArray(rawFields)) {
    return [];
  }

  return rawFields
    .map((field: SchemaFieldDefinition) => {
      const normalized = normalizeSchemaFieldType(field.type) as BuilderQuestionType;
      const normalizedType = QUESTION_TYPES.includes(normalized)
        ? normalized
        : "text";

      return {
        label: String(field.label ?? "").trim(),
        type: normalizedType,
        required: Boolean(field.required) && normalizedType !== "section",
        options: formatOptionsForInput(field.options),
        body: String(field.body ?? "").trim(),
        slug: String(field.slug ?? field.name ?? "").trim() || undefined,
        clinical_gate: Boolean(field.clinical_gate),
        clinical_gate_service_ids: Array.isArray(field.clinical_gate_service_ids)
          ? field.clinical_gate_service_ids.map(Number).filter((id) => id > 0)
          : [],
        clinical_gate_product_ids: Array.isArray(field.clinical_gate_product_ids)
          ? field.clinical_gate_product_ids.map(Number).filter((id) => id > 0)
          : [],
      };
    })
    .filter((question) => question.label.length > 0 || question.slug);
};

export const questionsToSchema = (
  questions: BuilderQuestion[]
): ClientRecordTemplateSchema => ({
  fields: questions.map((question, index) => {
    const field: SchemaFieldDefinition = {
      slug: question.slug ?? slugifyLabel(question.label, index),
      label: question.label.trim(),
      type: question.type as SchemaFieldType,
    };

    if (question.required && question.type !== "section") {
      field.required = true;
    }

    if (question.type === "select" && question.options.trim()) {
      field.options = question.options
        .split(",")
        .map((option) => option.trim())
        .filter(Boolean);
    }

    if (question.type === "consent_text" && (question.body ?? "").trim()) {
      field.body = (question.body ?? "").trim();
    }

    if (
      (question.type === "boolean" || question.type === "select") &&
      question.clinical_gate
    ) {
      field.clinical_gate = true;
      field.clinical_gate_trigger = true;
      field.clinical_gate_service_ids = Array.isArray(question.clinical_gate_service_ids)
        ? question.clinical_gate_service_ids.filter((id) => id > 0)
        : [];
      field.clinical_gate_product_ids = Array.isArray(question.clinical_gate_product_ids)
        ? question.clinical_gate_product_ids.filter((id) => id > 0)
        : [];
    }

    return field;
  }),
});

const mapTemplateToFormValues = (template: ClientRecordTemplate): TemplateFormValues => ({
  title: template.title,
  record_type: template.record_type === "evolution" ? "evolution" : "anamnesis",
  questions: schemaToQuestions(template.schema_json),
  is_active: template.is_active,
});

const AnamnesisTemplatesPage = () => {
  const { t } = useTranslation();

  document.title = t("settings.anamnesis_templates.pageTitle");

  const [templates, setTemplates] = useState<ClientRecordTemplate[]>([]);
  const [presets, setPresets] = useState<ClientRecordTemplatePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [copyingPresetId, setCopyingPresetId] = useState<number | null>(null);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ClientRecordTemplate | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ClientRecordTemplate | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [consentTerms, setConsentTerms] = useState<ConsentTerm[]>([]);
  const [serviceOptions, setServiceOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [productOptions, setProductOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [editingTermId, setEditingTermId] = useState<number | null>(null);
  const [termDraft, setTermDraft] = useState({ title: "", body_text: "" });
  const [savingTerm, setSavingTerm] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string()
          .trim()
          .required(t("settings.anamnesis_templates.validation.titleRequired")),
        questions: Yup.array()
          .min(1, t("studio.anamnesis.builder.validation.questionsMin"))
          .of(
            Yup.object({
              label: Yup.string()
                .trim()
                .required(t("studio.anamnesis.builder.validation.labelRequired")),
              type: Yup.string()
                .oneOf(QUESTION_TYPES)
                .required(t("studio.anamnesis.builder.validation.typeRequired")),
              required: Yup.boolean(),
              options: Yup.string().when("type", {
                is: "select",
                then: (schema) =>
                  schema
                    .trim()
                    .required(t("studio.anamnesis.builder.validation.optionsRequired"))
                    .test(
                      "has-options",
                      t("studio.anamnesis.builder.validation.optionsInvalid"),
                      (value) => {
                        if (!value) {
                          return false;
                        }

                        return value
                          .split(",")
                          .map((option) => option.trim())
                          .filter(Boolean).length > 0;
                      }
                    ),
                otherwise: (schema) => schema,
              }),
              body: Yup.string().when("type", {
                is: "consent_text",
                then: (schema) =>
                  schema
                    .trim()
                    .required(t("studio.anamnesis.builder.validation.termBodyRequired")),
                otherwise: (schema) => schema,
              }),
            })
          ),
        is_active: Yup.boolean(),
      }),
    [t]
  );

  const fetchTemplates = useCallback(async () => {
    setLoading(true);

    try {
      const [templatesResponse, presetsResponse, termsResponse, servicesResponse, productsResponse] =
        await Promise.all([
          getAll({ page: 1, per_page: PER_PAGE }),
          getTemplatePresets({ page: 1, per_page: PER_PAGE }),
          getConsentTerms(),
          getServices({ page: 1, per_page: 100 }),
          getProducts({ page: 1, per_page: 100 }),
        ]);
      setTemplates(templatesResponse.data);
      setPresets(presetsResponse.data);
      setConsentTerms(Array.isArray(termsResponse.data) ? termsResponse.data : []);
      setServiceOptions(
        (servicesResponse.data ?? []).map((service) => ({
          id: service.id,
          name: service.name,
        }))
      );
      setProductOptions(
        (productsResponse.data ?? []).map((product) => ({
          id: product.id,
          name: product.name,
        }))
      );
    } catch (error: any) {
      toast.error(error?.data || t("settings.anamnesis_templates.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const specialtyLabel = (specialty: string): string =>
    t(`settings.anamnesis_templates.presets.specialty.${specialty}`, specialty);

  const presetTitleLabel = (preset: ClientRecordTemplatePreset): string =>
    t(
      `settings.anamnesis_templates.presets.titles.${preset.slug}`,
      preset.title
    );

  const presetBlurbLabel = (preset: ClientRecordTemplatePreset): string =>
    t(
      `settings.anamnesis_templates.presets.blurbs.${preset.slug}`,
      t("settings.anamnesis_templates.presets.copyHint")
    );

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const formInitialValues = useMemo(
    () =>
      editingTemplate
        ? mapTemplateToFormValues(editingTemplate)
        : emptyFormValues(),
    [editingTemplate]
  );

  const formik = useFormik<TemplateFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);

      try {
        const schema_json = questionsToSchema(values.questions);
        const payload = {
          title: values.title.trim(),
          record_type: values.record_type,
          schema_json,
          is_active: values.is_active,
        };

        if (editingTemplate) {
          const response = await update(editingTemplate.id, payload);
          toast.success(response.message);
        } else {
          const response = await create(payload);
          toast.success(response.message);
        }

        closeModal();
        await fetchTemplates();
      } catch (error: any) {
        toast.error(error?.data || t("settings.anamnesis_templates.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
    setFormLoading(false);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setFormLoading(false);
    setModalOpen(true);
  };

  const openEdit = async (template: ClientRecordTemplate) => {
    setEditingTemplate(null);
    setFormLoading(true);
    setModalOpen(true);

    try {
      const response = await getById(template.id);
      setEditingTemplate(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.anamnesis_templates.toast.loadError"));
      closeModal();
    } finally {
      setFormLoading(false);
    }
  };

  const handleUsePreset = async (presetId: number) => {
    setCopyingPresetId(presetId);

    try {
      const response = await copyFromPreset(presetId);
      toast.success(t("settings.anamnesis_templates.toast.copyPresetSuccess"));
      setPresetModalOpen(false);
      await fetchTemplates();
      // Próximo passo leigo: revisar/ajustar o modelo copiado no builder.
      await openEdit(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.anamnesis_templates.toast.copyPresetError"));
    } finally {
      setCopyingPresetId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const response = await deleteTemplate(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      await fetchTemplates();
    } catch (error: any) {
      toast.error(error?.data || t("settings.anamnesis_templates.toast.deleteError"));
    }
  };

  const saveConsentTerm = async () => {
    if (editingTermId === null) {
      return;
    }

    setSavingTerm(true);
    try {
      const response = await updateConsentTerm(editingTermId, {
        title: termDraft.title.trim(),
        body_text: termDraft.body_text.trim(),
      });
      toast.success(response.message || t("settings.anamnesis_templates.terms.toastSaved"));
      setEditingTermId(null);
      setTermDraft({ title: "", body_text: "" });
      const termsResponse = await getConsentTerms();
      setConsentTerms(Array.isArray(termsResponse.data) ? termsResponse.data : []);
    } catch (error: any) {
      toast.error(error?.data || t("settings.anamnesis_templates.terms.toastError"));
    } finally {
      setSavingTerm(false);
    }
  };

  const getQuestionError = (
    index: number,
    field: keyof BuilderQuestion
  ): string | undefined => {
    const touched = formik.touched.questions?.[index];
    const errors = formik.errors.questions;

    if (!touched?.[field] || !Array.isArray(errors)) {
      return undefined;
    }

    const questionError = errors[index];

    if (!questionError || typeof questionError === "string") {
      return undefined;
    }

    return questionError[field];
  };

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("settings.anamnesis_templates.breadcrumb.title")}
            pageTitle={t("settings.anamnesis_templates.breadcrumb.pageTitle")}
          />
          <div className="d-flex justify-content-end mb-3">
            <HelpArticleLink articlePath={HELP_ARTICLE_ANAMNESIS} />
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {t("settings.anamnesis_templates.card.title")}
                  </h4>
                  {presets.length > 0 ? (
                    <Button
                      color="soft-primary"
                      onClick={() => setPresetModalOpen(true)}
                      disabled={copyingPresetId !== null}
                    >
                      <i className="ri-file-copy-line align-bottom me-1" />
                      {t("settings.anamnesis_templates.presets.usePresetShort")}
                    </Button>
                  ) : null}
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("settings.anamnesis_templates.actions.new")}
                  </Button>
                </CardHeader>

                <CardBody>
                  <Alert color="info" className="mb-4 py-2">
                    <div className="d-flex align-items-start gap-2">
                      <i className="ri-information-line fs-18 mt-1 flex-shrink-0" />
                      <span className="mb-0">{t("studio.anamnesis.banner.message")}</span>
                    </div>
                  </Alert>

                  <Alert color="light" className="mb-4 py-2 border">
                    <div className="d-flex align-items-start gap-2">
                      <i className="ri-file-paper-2-line fs-18 mt-1 flex-shrink-0 text-muted" />
                      <span className="mb-0 small text-muted">
                        {t("studio.anamnesis.banner.pdfTermsF2")}
                      </span>
                    </div>
                  </Alert>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="py-2">
                      <div className="text-center py-4">
                        <div className="avatar-lg mx-auto mb-4">
                          <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                            <i className="ri-health-book-line" />
                          </span>
                        </div>
                        <h5 className="mb-2">{t("studio.anamnesis.emptyState.title")}</h5>
                        <p className="text-muted mb-4 mx-auto" style={{ maxWidth: 480 }}>
                          {t("studio.anamnesis.emptyState.subtitle")}
                        </p>
                        <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                          {presets.length > 0 ? (
                            <Button
                              color="primary"
                              onClick={() => setPresetModalOpen(true)}
                              disabled={copyingPresetId !== null}
                            >
                              <i className="ri-gallery-line align-bottom me-1" />
                              {t("studio.anamnesis.emptyState.exploreGallery")}
                            </Button>
                          ) : (
                            <Button color="primary" onClick={openCreate}>
                              <i className="ri-add-line align-bottom me-1" />
                              {t("studio.anamnesis.emptyState.createFirst")}
                            </Button>
                          )}
                          {presets.length > 0 ? (
                            <Button color="soft-primary" onClick={openCreate}>
                              {t("studio.anamnesis.emptyState.createFromScratch")}
                            </Button>
                          ) : null}
                        </div>
                      </div>

                      {presets.length > 0 ? (
                        <>
                          <p className="text-muted text-center small mb-3">
                            {t("studio.anamnesis.emptyState.quickActivateHint")}
                          </p>
                          <Row className="g-3">
                            {presets.map((preset) => (
                              <Col md={6} xl={4} key={preset.id}>
                                <Card className="border shadow-none h-100">
                                  <CardBody className="d-flex flex-column">
                                    <Badge color="soft-info" className="mb-2 align-self-start">
                                      {specialtyLabel(preset.specialty)}
                                    </Badge>
                                    <h5 className="fs-15 mb-2">
                                      {presetTitleLabel(preset)}
                                    </h5>
                                    <p className="text-muted small flex-grow-1 mb-3">
                                      {presetBlurbLabel(preset)}
                                    </p>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      disabled={copyingPresetId === preset.id}
                                      onClick={() => handleUsePreset(preset.id)}
                                    >
                                      {copyingPresetId === preset.id ? (
                                        <Spinner size="sm" className="me-1" />
                                      ) : (
                                        <i className="ri-file-copy-line align-bottom me-1" />
                                      )}
                                      {t("studio.anamnesis.emptyState.activateModel")}
                                    </Button>
                                  </CardBody>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("settings.anamnesis_templates.table.title")}</th>
                            <th>{t("settings.anamnesis_templates.table.recordType")}</th>
                            <th>{t("settings.anamnesis_templates.table.status")}</th>
                            <th>{t("settings.anamnesis_templates.table.updatedAt")}</th>
                            <th className="text-end" style={{ width: 120 }}>
                              {t("settings.anamnesis_templates.table.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.map((template) => (
                            <tr key={template.id}>
                              <td className="fw-medium">{template.title}</td>
                              <td>
                                <Badge
                                  color={
                                    template.record_type === "evolution"
                                      ? "info"
                                      : "secondary"
                                  }
                                >
                                  {t(
                                    `settings.anamnesis_templates.recordType.${
                                      template.record_type === "evolution"
                                        ? "evolution"
                                        : "anamnesis"
                                    }`
                                  )}
                                </Badge>
                              </td>
                              <td>
                                <Badge color={template.is_active ? "success" : "secondary"}>
                                  {template.is_active
                                    ? t("settings.anamnesis_templates.status.active")
                                    : t("settings.anamnesis_templates.status.inactive")}
                                </Badge>
                              </td>
                              <td className="text-muted">
                                {new Intl.DateTimeFormat(undefined, {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                }).format(new Date(template.updated_at))}
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-1"
                                  title={t("settings.anamnesis_templates.actions.edit")}
                                  onClick={() => openEdit(template)}
                                >
                                  <i className="ri-pencil-line" />
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  title={t("settings.anamnesis_templates.actions.delete")}
                                  onClick={() => {
                                    setDeleteTarget(template);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {t("settings.anamnesis_templates.terms.title")}
                  </h4>
                  <p className="text-muted mb-0 mt-2 small">
                    {t("settings.anamnesis_templates.terms.hint")}
                  </p>
                </CardHeader>
                <CardBody>
                  {consentTerms.length === 0 ? (
                    <p className="text-muted mb-0">
                      {t("settings.anamnesis_templates.terms.empty")}
                    </p>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {consentTerms.map((term) => (
                        <Card key={term.id} className="border shadow-none mb-0">
                          <CardBody className="p-3">
                            {editingTermId === term.id ? (
                              <>
                                <div className="mb-3">
                                  <Label htmlFor={`term-title-${term.id}`}>
                                    {t("settings.anamnesis_templates.terms.fields.title")}
                                  </Label>
                                  <Input
                                    id={`term-title-${term.id}`}
                                    value={termDraft.title}
                                    onChange={(event) =>
                                      setTermDraft((prev) => ({
                                        ...prev,
                                        title: event.target.value,
                                      }))
                                    }
                                  />
                                </div>
                                <div className="mb-3">
                                  <Label htmlFor={`term-body-${term.id}`}>
                                    {t("settings.anamnesis_templates.terms.fields.body")}
                                  </Label>
                                  <Input
                                    id={`term-body-${term.id}`}
                                    type="textarea"
                                    rows={5}
                                    value={termDraft.body_text}
                                    onChange={(event) =>
                                      setTermDraft((prev) => ({
                                        ...prev,
                                        body_text: event.target.value,
                                      }))
                                    }
                                  />
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    disabled={savingTerm}
                                    onClick={saveConsentTerm}
                                  >
                                    {savingTerm ? (
                                      <Spinner size="sm" className="me-1" />
                                    ) : null}
                                    {t("settings.anamnesis_templates.terms.actions.save")}
                                  </Button>
                                  <Button
                                    color="light"
                                    size="sm"
                                    disabled={savingTerm}
                                    onClick={() => setEditingTermId(null)}
                                  >
                                    {t("settings.anamnesis_templates.terms.actions.cancel")}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <div>
                                    <Badge
                                      color={
                                        term.purpose === "image_marketing"
                                          ? "soft-success"
                                          : "soft-info"
                                      }
                                      className="mb-2"
                                    >
                                      {t(
                                        `settings.anamnesis_templates.terms.purpose.${term.purpose || "procedure"}`,
                                        specialtyLabel(term.specialty)
                                      )}
                                    </Badge>
                                    <Badge color="soft-info" className="mb-2 ms-1">
                                      {specialtyLabel(term.specialty)}
                                    </Badge>
                                    {term.version ? (
                                      <Badge color="soft-secondary" className="mb-2 ms-1">
                                        {t("settings.anamnesis_templates.terms.version", {
                                          version: term.version,
                                        })}
                                      </Badge>
                                    ) : null}
                                    <h5 className="fs-15 mb-2">{term.title}</h5>
                                    <p
                                      className="text-muted small mb-0"
                                      style={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {term.body_text}
                                    </p>
                                  </div>
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    onClick={() => {
                                      setEditingTermId(term.id);
                                      setTermDraft({
                                        title: term.title,
                                        body_text: term.body_text,
                                      });
                                    }}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                </div>
                              </>
                            )}
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <ModalHeader toggle={closeModal}>
          {editingTemplate
            ? t("settings.anamnesis_templates.form.editTitle")
            : t("settings.anamnesis_templates.form.createTitle")}
        </ModalHeader>
        <ModalBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <FormikProvider value={formik}>
            <Form
              key={entityFormKey(editingTemplate?.id)}
              onSubmit={(event) => {
                event.preventDefault();
                formik.handleSubmit();
              }}
            >
              <div className="mb-3">
                <Label htmlFor="template-title">
                  {t("settings.anamnesis_templates.fields.title.label")}
                </Label>
                <Input
                  id="template-title"
                  name="title"
                  placeholder={t("settings.anamnesis_templates.fields.title.placeholder")}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.title && formik.errors.title)}
                />
                {formik.touched.title && formik.errors.title ? (
                  <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
                ) : null}
              </div>

              <div className="mb-3">
                <Label htmlFor="template-record-type">
                  {t("settings.anamnesis_templates.fields.recordType.label")}
                </Label>
                <Input
                  id="template-record-type"
                  name="record_type"
                  type="select"
                  value={formik.values.record_type}
                  onChange={formik.handleChange}
                >
                  <option value="anamnesis">
                    {t("settings.anamnesis_templates.recordType.anamnesis")}
                  </option>
                  <option value="evolution">
                    {t("settings.anamnesis_templates.recordType.evolution")}
                  </option>
                </Input>
                <p className="text-muted small mb-0 mt-1">
                  {t("settings.anamnesis_templates.fields.recordType.help")}
                </p>
              </div>

              <AnamnesisTemplateBuilderFields
                getQuestionError={getQuestionError}
                consentTerms={consentTerms}
                serviceOptions={serviceOptions}
                productOptions={productOptions}
              />

              <div className="form-check form-switch mb-4">
                <Input
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  id="template-active"
                  name="is_active"
                  checked={formik.values.is_active}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" htmlFor="template-active">
                  {t("settings.anamnesis_templates.fields.isActive")}
                </Label>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <Button color="light" type="button" onClick={closeModal}>
                  {t("settings.anamnesis_templates.actions.cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-save-line align-bottom me-1" />
                  )}
                  {t("settings.anamnesis_templates.actions.save")}
                </Button>
              </div>
            </Form>
          </FormikProvider>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={presetModalOpen} toggle={() => setPresetModalOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setPresetModalOpen(false)}>
          {t("settings.anamnesis_templates.presets.modalTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">{t("settings.anamnesis_templates.presets.modalDescription")}</p>
          <Row className="g-3">
            {presets.map((preset) => (
              <Col md={6} key={preset.id}>
                <Card className="border shadow-none h-100">
                  <CardBody className="d-flex flex-column">
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      <Badge color="soft-info" className="align-self-start">
                        {specialtyLabel(preset.specialty)}
                      </Badge>
                      <Badge
                        color={
                          preset.record_type === "evolution"
                            ? "info"
                            : "secondary"
                        }
                        className="align-self-start"
                      >
                        {t(
                          `settings.anamnesis_templates.recordType.${
                            preset.record_type === "evolution"
                              ? "evolution"
                              : "anamnesis"
                          }`
                        )}
                      </Badge>
                    </div>
                    <h5 className="fs-15 mb-2">
                      {presetTitleLabel(preset)}
                    </h5>
                    <p className="text-muted small flex-grow-1 mb-3">
                      {presetBlurbLabel(preset)}
                    </p>
                    <Button
                      color="primary"
                      size="sm"
                      disabled={copyingPresetId === preset.id}
                      onClick={() => handleUsePreset(preset.id)}
                    >
                      {copyingPresetId === preset.id ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      {t("settings.anamnesis_templates.presets.usePreset")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("settings.anamnesis_templates.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deleteTarget
                  ? t("settings.anamnesis_templates.delete.description", {
                      title: deleteTarget.title,
                    })
                  : t("settings.anamnesis_templates.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("settings.anamnesis_templates.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("settings.anamnesis_templates.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AnamnesisTemplatesPage;
